// src/pages/MovieManiaHome.jsx
// Full-feature MovieMania module (demo frontend-only)
// Requires: TailwindCSS + framer-motion

import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// ---- CONFIG ----
const INDUSTRIES = [
  { id: "bollywood", label: "Bollywood" },
  { id: "tollywood", label: "Tollywood" },
  { id: "kollywood", label: "Kollywood" },
  { id: "mollywood", label: "Mollywood" },
];

const SUBTABS = [
  { id: "contest", label: "🎤 GeetMala Contest" },
  { id: "listen", label: "🎧 Listen Song" },
];

const DECADES = [
  "1950", "1960s", "1970s", "1980s", "1990s",
  "2000s", "2010s", "2020s", "2025s"
];

// Dummy data: 5 songs per industry distributed by decade
const DUMMY_SONGS = {
  bollywood: {
    "1960s": [
      { id: "b601", title: "Mere Sapno Ki Rani", singer: "Kishore Kumar", year: 1969, youtube: "https://youtu.be/example601" },
      { id: "b602", title: "Lag Ja Gale", singer: "Lata Mangeshkar", year: 1964, youtube: "https://youtu.be/example602" },
    ],
    "1970s": [
      { id: "b701", title: "Dum Maro Dum", singer: "Asha Bhosle", year: 1971, youtube: "https://youtu.be/example701" },
      { id: "b702", title: "Chura Liya Hai Tumne", singer: "Asha Bhosle", year: 1973, youtube: "https://youtu.be/example702" },
    ],
    "1990s": [
      { id: "b901", title: "Pehla Nasha", singer: "Udit Narayan", year: 1992, youtube: "https://youtu.be/example901" },
    ],
  },
  tollywood: {
    "1980s": [
      { id: "t801", title: "Chiru Hit Song 1", singer: "SP Balasubrahmanyam", year: 1986, youtube: "https://youtu.be/examplet801" },
      { id: "t802", title: "Tollywood Melody 2", singer: "S Janaki", year: 1989, youtube: "https://youtu.be/examplet802" },
    ],
    "2000s": [
      { id: "t001", title: "Aadavari Matalaku", singer: "SP Charan", year: 2002, youtube: "https://youtu.be/examplet001" },
      { id: "t002", title: "Nuvvostanante Nenoddantana", singer: "Kunal Ganjawala", year: 2005, youtube: "https://youtu.be/examplet002" },
      { id: "t003", title: "Bommarillu", singer: "Sid Sriram", year: 2006, youtube: "https://youtu.be/examplet003" },
    ],
  },
  kollywood: {
    "1990s": [
      { id: "k901", title: "Ennavale Adi Ennavale", singer: "SPB", year: 1994, youtube: "https://youtu.be/examplek901" },
      { id: "k902", title: "Poove Sempoove", singer: "KJ Yesudas", year: 1992, youtube: "https://youtu.be/examplek902" },
    ],
    "2010s": [
      { id: "k101", title: "Why This Kolaveri Di", singer: "Dhanush", year: 2011, youtube: "https://youtu.be/examplek101" },
      { id: "k102", title: "Munbe Vaa", singer: "Shreya Ghoshal", year: 2010, youtube: "https://youtu.be/examplek102" },
    ],
    "2020s": [
      { id: "k202", title: "Arabic Kuthu", singer: "Anirudh", year: 2022, youtube: "https://youtu.be/examplek202" },
    ],
  },
  mollywood: {
    "1970s": [
      { id: "m701", title: "Manathe Chandanakkeeru", singer: "KJ Yesudas", year: 1978, youtube: "https://youtu.be/examplem701" },
    ],
    "1980s": [
      { id: "m801", title: "Ezhuthiyatharanu", singer: "KJ Yesudas", year: 1982, youtube: "https://youtu.be/examplem801" },
      { id: "m802", title: "Thenum Vayambum", singer: "S Janaki", year: 1986, youtube: "https://youtu.be/examplem802" },
    ],
    "2010s": [
      { id: "m101", title: "Jimikki Kammal", singer: "Vineeth Sreenivasan", year: 2017, youtube: "https://youtu.be/examplem101" },
      { id: "m102", title: "Malare", singer: "Vijay Yesudas", year: 2015, youtube: "https://youtu.be/examplem102" },
    ],
  },
};

// LocalStorage keys
const LS_FAVORITE = "moviemania_favorite_v1"; // only one favorite allowed
const LS_LISTENS = "moviemania_listens_v1";

const loadJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};
const saveJSON = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {}
};

export default function MovieManiaHome() {
  const [activeIndustry, setActiveIndustry] = useState("bollywood");
  const [activeSub, setActiveSub] = useState("contest");
  const [activeDecade, setActiveDecade] = useState("1970s");

  const [favoriteSong, setFavoriteSong] = useState(() => loadJSON(LS_FAVORITE, null));
  const [listenCounts, setListenCounts] = useState(() => loadJSON(LS_LISTENS, {}));

  useEffect(() => saveJSON(LS_FAVORITE, favoriteSong), [favoriteSong]);
  useEffect(() => saveJSON(LS_LISTENS, listenCounts), [listenCounts]);

  const songs = useMemo(() => {
    const data = DUMMY_SONGS[activeIndustry]?.[activeDecade] || [];
    return data;
  }, [activeIndustry, activeDecade]);

  const handleVote = (songId) => {
    if (favoriteSong && favoriteSong !== songId) return alert("❗ You can select only one favorite song.");
    setFavoriteSong(songId);
  };

  const handleListen = (song) => {
    setListenCounts((prev) => ({
      ...prev,
      [song.id]: (prev[song.id] || 0) + 1,
    }));
    window.open(song.youtube, "_blank");
  };

  const getListenCount = (id) => listenCounts[id] || 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 via-black to-gray-900 text-white p-4">
      <motion.h1
        className="text-center text-3xl font-bold mb-4"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
      >
        🎬 MovieMania
      </motion.h1>

      {/* Industry Tabs */}
      <div className="flex gap-2 overflow-x-auto mb-4">
        {INDUSTRIES.map((i) => (
          <button
            key={i.id}
            onClick={() => setActiveIndustry(i.id)}
            className={`px-4 py-2 rounded-full font-semibold ${
              activeIndustry === i.id
                ? "bg-yellow-400 text-black"
                : "bg-gray-800 hover:bg-gray-700"
            }`}
          >
            {i.label}
          </button>
        ))}
      </div>

      {/* Sub Tabs */}
      <div className="flex gap-2 justify-center mb-4">
        {SUBTABS.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveSub(s.id)}
            className={`px-3 py-1 rounded-lg ${
              activeSub === s.id
                ? "bg-pink-500 text-white"
                : "bg-gray-700 hover:bg-gray-600"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Decade Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-6 text-center">
        {DECADES.map((d) => (
          <button
            key={d}
            onClick={() => setActiveDecade(d)}
            className={`py-2 rounded-lg ${
              activeDecade === d
                ? "bg-green-400 text-black font-bold"
                : "bg-gray-700 hover:bg-gray-600"
            }`}
          >
            {d}
          </button>
        ))}
      </div>

      {/* Songs */}
      <div className="grid grid-cols-1 gap-3 max-w-md mx-auto">
        <AnimatePresence>
          {songs.length === 0 ? (
            <motion.div
              key="no-song"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center text-gray-400 p-4"
            >
              No songs available for this decade.
            </motion.div>
          ) : (
            songs.map((song) => (
              <motion.div
                key={song.id}
                whileHover={{ scale: 1.02 }}
                className="bg-gray-900 rounded-xl p-4 shadow-md"
              >
                <h3 className="text-lg font-semibold">{song.title}</h3>
                <p className="text-sm text-gray-400">{song.singer} • {song.year}</p>

                {/* Show current listen count before click */}
                <p className="text-xs mt-1 text-gray-400">
                  Views: {getListenCount(song.id)}
                </p>

                {activeSub === "contest" ? (
                  <button
                    onClick={() => handleVote(song.id)}
                    className={`mt-2 px-3 py-1 rounded-md w-full ${
                      favoriteSong === song.id
                        ? "bg-green-600 text-white"
                        : "bg-blue-600 hover:bg-blue-500"
                    }`}
                    disabled={favoriteSong && favoriteSong !== song.id}
                  >
                    {favoriteSong === song.id ? "✅ Your Favorite" : "❤️ Vote As My Favorite"}
                  </button>
                ) : (
                  <button
                    onClick={() => handleListen(song)}
                    className="mt-2 px-3 py-1 w-full bg-red-500 hover:bg-red-400 rounded-md"
                  >
                    ▶️ Listen on YouTube
                  </button>
                )}
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      <footer className="text-center text-xs mt-8 text-gray-400">
        By Think5Whys Global DeepTech Solutions • Demo Mode
      </footer>
    </div>
  );
}
